import { Stack, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { convertToMoney } from '@woovi/utils';

import type { PaymentLinkExpiredValue_charge$key } from './__generated__/PaymentLinkExpiredValue_charge.graphql.ts';

type PaymentLinkExpiredValueProps = {
  charge: PaymentLinkExpiredValue_charge$key;
};
const PaymentLinkExpiredValue = (
  props: PaymentLinkExpiredValueProps,
): ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation();
  const charge = useFragment<PaymentLinkExpiredValue_charge$key>(
    graphql`
      fragment PaymentLinkExpiredValue_charge on Charge {
        id
        discount
        value
        valueWithDiscount
      }
    `,
    props.charge,
  );

  const value = convertToMoney(charge.value);

  const hasDiscount = charge?.discount > 0;

  const getValue = (): ReactNode => {
    if (hasDiscount) {
      const finalValue = convertToMoney(charge?.valueWithDiscount as number);

      return (
        <Stack direction='row' sx={{ gap: 0.5 }}>
          <Typography
            component='span'
            textAlign='center'
            variant='h6'
            sx={{
              textDecoration: 'line-through',
              color: theme.palette.text.disabled,
            }}
          >
            {value}
          </Typography>
          <Typography
            textAlign='center'
            variant='h6'
            sx={{ color: theme.palette.text.secondary }}
          >
            {finalValue}
          </Typography>
        </Stack>
      );
    }

    return (
      <Typography
        textAlign='center'
        variant='h6'
        sx={{ color: theme.palette.text.secondary }}
      >
        {value}
      </Typography>
    );
  };

  return (
    <Stack>
      <Typography textAlign='center' variant='h6' fontWeight='bold'>
        {t('Value')}
      </Typography>
      {getValue()}
    </Stack>
  );
};

export default PaymentLinkExpiredValue;
